import { updateCameraResolution } from '../api/api-setting-meeting';
import { captureSizes, readCameraResolutionPolicy } from '../hooks/useCameraResolutionPolicy';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Room,
  RoomEvent,
  createLocalAudioTrack,
  Track,
  VideoPresets,
} from "livekit-client";
import {
  ChevronsUpDown,
  LogOut,
  MessageSquare,
  Mic,
  MicOff,
  Monitor,
  Settings,
  Share2,
  Video,
  VideoOff,
  Check,
  Radio,
  Circle,
  Hand,
} from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { LocalAudioTrack, LocalVideoTrack } from "livekit-client";
import { ScreenAudioProcessor } from "../helper/audioProcessor";
import { Meeting } from "@/modules/meetings/types/meeting.type";
import { RecordSheet } from "./RecordSheet";
import { StreamSheet } from "./StreamSheet";
import { useLiveKit } from "../contexts";
import { updateRaiseHandMeeting } from "../api/api-setting-meeting";
import { toast } from "sonner";
import { ApiError } from "@/api/api-axios";

let audioProcessor: ScreenAudioProcessor | null = null;
let screenShareStream: MediaStream | null = null;
interface BottomControlBarProps {
  room: Room;
  onToggleChat: () => void;
  onToggleControlPanel: () => void;
  onLeave: () => void;
  isHost: boolean;
  onLinkMeeting: () => void;
  meeting: Meeting | null;
}
type CameraQuality = "480p" | "960p" | "240p" | "90p"|"180p"|"216p"|"360p" | "720p" | "1080p" | "2K" | "4K";

/** Đồng bộ với VideoPresets của livekit-client (VideoResolution + VideoEncoding) */
const CAMERA_PRESETS: Array<{
  id: CameraQuality;
  label: string;
  resolution: { width: number; height: number; frameRate?: number };
  videoEncoding: { maxBitrate: number; maxFramerate?: number };
}> = [
  { id: "90p", label: "Thấp (90p)", resolution: VideoPresets.h90.resolution, videoEncoding: VideoPresets.h90.encoding },
  { id: "180p", label: "Thấp (180p)", resolution: VideoPresets.h180.resolution, videoEncoding: VideoPresets.h180.encoding },
  { id: "240p", label: "Tiết kiệm (240p)", resolution: {width:426,height:240,frameRate:30}, videoEncoding: {maxBitrate:200_000,maxFramerate:30} },
  { id: "216p", label: "Thấp (216p)", resolution: VideoPresets.h216.resolution, videoEncoding: VideoPresets.h216.encoding },
  { id: "360p", label: "Thấp (360p)", resolution: VideoPresets.h360.resolution, videoEncoding: VideoPresets.h360.encoding },
  { id: "480p", label: "480p", resolution: {width:854,height:480,frameRate:30}, videoEncoding: {maxBitrate:800_000,maxFramerate:30} },
  { id: "960p", label: "960p", resolution: {width:1280,height:960,frameRate:30}, videoEncoding: {maxBitrate:2_000_000,maxFramerate:30} },
  { id: "720p", label: "Trung bình (720p)", resolution: VideoPresets.h720.resolution, videoEncoding: VideoPresets.h720.encoding },
  { id: "1080p", label: "Cao (1080p)", resolution: VideoPresets.h1080.resolution, videoEncoding: VideoPresets.h1080.encoding },
  { id: "2K", label: "2K", resolution: VideoPresets.h1440.resolution, videoEncoding: VideoPresets.h1440.encoding },
  { id: "4K", label: "4K", resolution: VideoPresets.h2160.resolution, videoEncoding: VideoPresets.h2160.encoding },
];

export function BottomControlBar({
  room,
  onToggleChat,
  onToggleControlPanel,
  onLeave,
  isHost,
  onLinkMeeting,
  meeting,
}: BottomControlBarProps) {
  const localParticipant = room.localParticipant;
  const {
    refreshMeeting,
    currentUserRaisedHand,
    currentUserCanShare,
    isActionsLocked,
    cameraResolutionPolicy,
  } = useLiveKit();
  console.log("currentUserRaisedHand", currentUserRaisedHand);
  console.log("currentUserCanShare", currentUserCanShare);

  // State để lưu trạng thái mic, camera, và screen share
  const [isMicEnabled, setIsMicEnabled] = useState(
    localParticipant.isMicrophoneEnabled
  );
  const [isCameraEnabled, setIsCameraEnabled] = useState(
    localParticipant.isCameraEnabled
  );
  const [isScreenShareEnabled, setIsScreenShareEnabled] = useState(
    localParticipant.isScreenShareEnabled
  );

  // State để quản lý RecordSheet và StreamSheet
  const [isRecordSheetOpen, setIsRecordSheetOpen] = useState(false);
  const [isStreamSheetOpen, setIsStreamSheetOpen] = useState(false);

  // State để quản lý microphones
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<
    string | null
  >(null);
  const [isMicrophoneMenuOpen, setIsMicrophoneMenuOpen] = useState(false);
  const isMicrophoneInitialized = useRef(false);

  // State để quản lý cameras
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [isCameraMenuOpen, setIsCameraMenuOpen] = useState(false);
  const isCameraInitialized = useRef(false);
  const [cameraQuality, setCameraQuality] =
    useState<CameraQuality>("720p");

  useEffect(() => {
    const configured = cameraResolutionPolicy?.targets[localParticipant.identity]?.configuredResolution;
    if (configured) setCameraQuality((configured === '2160p' ? '4K' : configured === '1440p' ? '2K' : configured) as CameraQuality);
  }, [cameraResolutionPolicy, localParticipant.identity]);

  // Lấy danh sách microphones từ thiết bị
  useEffect(() => {
    const loadMicrophones = async () => {
      try {
        // Yêu cầu quyền truy cập microphone để lấy danh sách đầy đủ
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // Lấy danh sách tất cả devices
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Lọc chỉ lấy microphones
        const audioInputs = devices.filter(
          (device) => device.kind === "audioinput"
        );

        setMicrophones(audioInputs);

        // Lấy microphone hiện tại nếu có
        if (audioInputs.length > 0 && !selectedMicrophoneId) {
          const currentTrack = localParticipant.getTrackPublication(
            Track.Source.Microphone
          );
          if (currentTrack?.track instanceof MediaStreamTrack) {
            const settings = currentTrack.track.getSettings();
            if (settings.deviceId) {
              setSelectedMicrophoneId(settings.deviceId);
            } else {
              // Nếu không có deviceId, chọn microphone đầu tiên
              setSelectedMicrophoneId(audioInputs[0].deviceId);
            }
          } else {
            setSelectedMicrophoneId(audioInputs[0].deviceId);
          }
        }
      } catch (error) {
        console.error("Error loading microphones:", error);
      }
    };

    if (!isMicrophoneInitialized.current) {
      loadMicrophones();
      isMicrophoneInitialized.current = true;
    }

    // Lắng nghe khi có device mới được thêm vào
    const handleDeviceChange = () => {
      loadMicrophones();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [localParticipant, selectedMicrophoneId]);

  // Lấy danh sách cameras từ thiết bị
  useEffect(() => {
    const loadCameras = async () => {
      try {
        // Yêu cầu quyền truy cập camera để lấy danh sách đầy đủ
        await navigator.mediaDevices.getUserMedia({ video: true });

        // Lấy danh sách tất cả devices
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Lọc chỉ lấy cameras
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput"
        );

        setCameras(videoInputs);

        // Lấy camera hiện tại nếu có
        if (videoInputs.length > 0 && !selectedCameraId) {
          const currentTrack = localParticipant.getTrackPublication(
            Track.Source.Camera
          );
          if (currentTrack?.track instanceof MediaStreamTrack) {
            const settings = currentTrack.track.getSettings();
            if (settings.deviceId) {
              setSelectedCameraId(settings.deviceId);
            } else {
              // Nếu không có deviceId, chọn camera đầu tiên
              setSelectedCameraId(videoInputs[0].deviceId);
            }
          } else {
            setSelectedCameraId(videoInputs[0].deviceId);
          }
        }
      } catch (error) {
        console.error("Error loading cameras:", error);
      }
    };

    if (!isCameraInitialized.current) {
      loadCameras();
      isCameraInitialized.current = true;
    }

    // Lắng nghe khi có device mới được thêm vào
    const handleDeviceChange = () => {
      loadCameras();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [localParticipant, selectedCameraId]);

  // Lắng nghe sự kiện thay đổi track từ Room
  useEffect(() => {
    const updateTrackStates = () => {
      setIsMicEnabled(localParticipant.isMicrophoneEnabled);
      setIsCameraEnabled(localParticipant.isCameraEnabled);
      setIsScreenShareEnabled(localParticipant.isScreenShareEnabled);

      // Cập nhật selected microphone khi track thay đổi
      const micTrack = localParticipant.getTrackPublication(
        Track.Source.Microphone
      );
      if (micTrack?.track instanceof MediaStreamTrack) {
        const settings = micTrack.track.getSettings();
        if (settings.deviceId) {
          setSelectedMicrophoneId(settings.deviceId);
        }
      }

      // Cập nhật selected camera khi track thay đổi
      const cameraTrack = localParticipant.getTrackPublication(
        Track.Source.Camera
      );
      if (cameraTrack?.track instanceof MediaStreamTrack) {
        const settings = cameraTrack.track.getSettings();
        if (settings.deviceId) {
          setSelectedCameraId(settings.deviceId);
        }
      }
    };

    // Cập nhật trạng thái ban đầu
    updateTrackStates();

    // Lắng nghe các sự kiện khi track được publish/unpublish
    const handleTrackPublished = () => {
      updateTrackStates();
    };

    const handleTrackUnpublished = () => {
      updateTrackStates();
    };

    const handleLocalTrackPublished = () => {
      updateTrackStates();
    };

    const handleLocalTrackUnpublished = () => {
      updateTrackStates();
    };

    // Đăng ký các event listeners
    room.on(RoomEvent.TrackPublished, handleTrackPublished);
    room.on(RoomEvent.TrackUnpublished, handleTrackUnpublished);
    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

    // Cleanup
    return () => {
      room.off(RoomEvent.TrackPublished, handleTrackPublished);
      room.off(RoomEvent.TrackUnpublished, handleTrackUnpublished);
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
    };
  }, [room, localParticipant]);

  const handleToggleMic = useCallback(async () => {
    try {
      const enabled = !isMicEnabled;
      await localParticipant.setMicrophoneEnabled(enabled);
      // Cập nhật state ngay lập tức để UI phản hồi nhanh
      setIsMicEnabled(enabled);
    } catch (error) {
      console.error("Error toggling microphone:", error);
      // Nếu có lỗi, revert lại state
      setIsMicEnabled(localParticipant.isMicrophoneEnabled);
    }
  }, [localParticipant, isMicEnabled]);

  // Chuyển đổi microphone
  const handleSelectMicrophone = useCallback(
    async (deviceId: string) => {
      if (deviceId === selectedMicrophoneId) {
        setIsMicrophoneMenuOpen(false);
        return;
      }

      try {
        // Tạo track mới với microphone mới
        const newTrack = await createLocalAudioTrack({
          deviceId: deviceId,
        });

        // Lấy track cũ
        const oldTrack = localParticipant.getTrackPublication(
          Track.Source.Microphone
        );

        // Unpublish track cũ nếu có
        if (oldTrack && oldTrack.track) {
          await localParticipant.unpublishTrack(oldTrack.track);
        }

        // Publish track mới
        await localParticipant.publishTrack(newTrack, {
          source: Track.Source.Microphone,
        });

        // Cập nhật selected microphone
        setSelectedMicrophoneId(deviceId);
        setIsMicrophoneMenuOpen(false);
      } catch (error) {
        console.error("Error switching microphone:", error);
      }
    },
    [localParticipant, selectedMicrophoneId]
  );

  const handleToggleCamera = useCallback(async () => {
    try {
      const enabled = !isCameraEnabled;
      await localParticipant.setCameraEnabled(enabled);
      // Cập nhật state ngay lập tức để UI phản hồi nhanh
      setIsCameraEnabled(enabled);
    } catch (error) {
      console.error("Error toggling camera:", error);
      // Nếu có lỗi, revert lại state
      setIsCameraEnabled(localParticipant.isCameraEnabled);
    }
  }, [localParticipant, isCameraEnabled]);

  /** Chuyển đổi camera + chất lượng. Dùng VideoCaptureOptions (resolution) và TrackPublishOptions (videoEncoding, simulcast). */
  const handleSelectCamera = useCallback(
    async (deviceId: string, preset: CameraQuality = "720p") => {
      if (deviceId === selectedCameraId && preset === cameraQuality) {
        setIsCameraMenuOpen(false);
        return;
      }

      const presetConfig = CAMERA_PRESETS.find((p) => p.id === preset);
      if (!presetConfig) {
        console.error("Invalid camera preset:", preset);
        return;
      }

      try {
        if (!meeting) return;
        const normalized = preset === '4K' ? '2160p' : preset === '2K' ? '1440p' : preset;
        await updateCameraResolution(meeting.id, normalized);
        // Resolution-only changes are applied by the server policy listener on the existing track.
        if (deviceId !== selectedCameraId) {
          const oldPub = localParticipant.getTrackPublication(Track.Source.Camera);
          room.options.videoCaptureDefaults = {...room.options.videoCaptureDefaults, deviceId};
          if (oldPub?.track && oldPub.isMuted) await oldPub.track.setDeviceId(deviceId);
          if (oldPub?.track && !oldPub.isMuted) {
            const target = readCameraResolutionPolicy(room, meeting.id)?.targets[localParticipant.identity];
            const effective = captureSizes[target?.resolution ?? '180p'] ?? captureSizes['180p'];
            await oldPub.track.restartTrack({deviceId, resolution: {...presetConfig.resolution, ...effective, frameRate: target?.fps ?? 15}});
          }
        }

        setSelectedCameraId(deviceId);
        setCameraQuality(preset);
        setIsCameraMenuOpen(false);
      } catch (error) {
        console.error("Error switching camera:", error);
      }
    },
    [localParticipant, selectedCameraId, cameraQuality, meeting, room]
  );


  const handleShareScreen = useCallback(async () => {
    try {
      const enabled = !isScreenShareEnabled && currentUserCanShare;

      if (!enabled) {
        // Stop tất cả tracks trong stream gốc để browser ẩn thông báo
        if (screenShareStream) {
          screenShareStream.getTracks().forEach((track) => {
            track.stop();
          });
          screenShareStream = null;
        }

        // Unpublish tất cả screen share tracks
        const screenShareTracks = Array.from(
          localParticipant.trackPublications.values()
        ).filter((pub) => pub.source === Track.Source.ScreenShare);

        for (const trackPub of screenShareTracks) {
          if (trackPub.track) {
            await localParticipant.unpublishTrack(trackPub.track);
          }
        }

        await localParticipant.setScreenShareEnabled(false);
        audioProcessor?.stop();
        audioProcessor = null;
        setIsScreenShareEnabled(false);
        return;
      }

      if (!currentUserCanShare || isActionsLocked) return;

      // Cleanup các screen share tracks cũ trước khi publish track mới
      const existingScreenShareTracks = Array.from(
        localParticipant.trackPublications.values()
      ).filter((pub) => pub.source === Track.Source.ScreenShare);

      for (const trackPub of existingScreenShareTracks) {
        if (trackPub.track) {
          await localParticipant.unpublishTrack(trackPub.track);
        }
      }

      // Cleanup audio processor cũ nếu có
      if (audioProcessor) {
        audioProcessor.stop();
        audioProcessor = null;
      }

      // Yêu cầu quyền share screen (browser tự động cho phép chọn screen/window/tab)
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: "always",
        } as MediaTrackConstraints,
        audio: {
          // Audio constraints cho chất lượng tốt nhất
          echoCancellation: false, // Tắt vì screen audio không cần
          noiseSuppression: false, // Tắt để giữ chất lượng gốc
          autoGainControl: false, // Tắt vì ta có custom processor
          sampleRate: 48000, // CD quality
          sampleSize: 16,
          channelCount: 2, // Stereo
          latency: 0, // Low latency
        } as MediaTrackConstraints,
      });

      // Lưu reference đến stream để có thể stop sau này
      screenShareStream = stream;

      // Kiểm tra nếu user cancel hoặc không có video track
      if (!stream || !stream.getVideoTracks()[0]) {
        // Stop stream nếu có để cleanup
        if (screenShareStream) {
          screenShareStream.getTracks().forEach((track) => {
            track.stop();
          });
          screenShareStream = null;
        }
        setIsScreenShareEnabled(false);
        return;
      }

      const videoTrackRaw = stream.getVideoTracks()[0];

      // Xử lý khi user dừng sharing từ browser (click stop sharing)
      videoTrackRaw.onended = async () => {
        try {
          // Stop tất cả tracks trong stream để browser ẩn thông báo
          if (screenShareStream) {
            screenShareStream.getTracks().forEach((track) => {
              track.stop();
            });
            screenShareStream = null;
          }

          await localParticipant.setScreenShareEnabled(false);
          audioProcessor?.stop();
          audioProcessor = null;
          setIsScreenShareEnabled(false);
        } catch (err) {
          console.error("Error stopping screen share:", err);
        }
      };

      /** 🎥 Video track */
      const videoTrack = new LocalVideoTrack(videoTrackRaw, undefined, true);

      /** 🔊 Audio track + processor */
      const audioTrackRaw = stream.getAudioTracks()[0];
      if (audioTrackRaw) {
        // Xử lý khi audio track bị dừng
        audioTrackRaw.onended = async () => {
          try {
            if (audioProcessor) {
              audioProcessor.stop();
              audioProcessor = null;
            }
          } catch (err) {
            console.error("Error stopping audio processor:", err);
          }
        };

        audioProcessor = new ScreenAudioProcessor(audioTrackRaw);

        /** Auto gain loop */
        const gainLoop = () => {
          if (audioProcessor) {
            audioProcessor.autoAdjustGain();
            requestAnimationFrame(gainLoop);
          }
        };
        gainLoop();

        const processedAudioTrack = new LocalAudioTrack(
          audioProcessor.getProcessedTrack()
        );

        await localParticipant.publishTrack(processedAudioTrack, {
          source: Track.Source.ScreenShareAudio,
        });
      }

      await localParticipant.publishTrack(videoTrack, {
        source: Track.Source.ScreenShare,
        videoEncoding: {
          maxBitrate: 2_000_000, // KHÓA video lại
          maxFramerate: 15,
        },
        simulcast: false,
      });

      setIsScreenShareEnabled(true);
    } catch (err) {
      // Xử lý khi user cancel dialog hoặc có lỗi
      if (err instanceof Error && err.name === "NotAllowedError") {
        console.log("User denied screen share permission");
      } else if (err instanceof Error && err.name === "AbortError") {
        console.log("User cancelled screen share");
      } else {
        console.error("Screen share error:", err);
      }

      // Cleanup stream nếu có
      if (screenShareStream) {
        screenShareStream.getTracks().forEach((track) => {
          track.stop();
        });
        screenShareStream = null;
      }

      setIsScreenShareEnabled(false);
    }
  }, [localParticipant, isScreenShareEnabled, currentUserCanShare, isActionsLocked]);

  useEffect(() => {
    if (!currentUserCanShare && (isScreenShareEnabled || screenShareStream)) void handleShareScreen();
  }, [currentUserCanShare, isScreenShareEnabled, handleShareScreen]);


  // Handler cho Raise Hand (user tự giơ tay)
  const handleToggleRaiseHand = useCallback(async () => {
    if (!meeting?.id || !localParticipant?.identity) return;
    try {
      const action = currentUserRaisedHand ? "remove" : "add";
      const payload = {
        [action === "add" ? "addRaiseHand" : "removeRaiseHand"]: [
          localParticipant.identity,
        ],
      };
      const response = await updateRaiseHandMeeting(meeting.id, payload);
      console.log("response", response);
      await refreshMeeting();
      
    } catch (error) {
      console.error("Error toggling raise hand:", error);
      toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? "Ko có user trong currentRaisedHands"}`);
    }
  }, [meeting?.id, localParticipant?.identity, currentUserRaisedHand, refreshMeeting]);

  return (
    <div className="flex items-center justify-between gap-1 sm:gap-2 bg-[#0f0f0f] px-2 py-2 overflow-x-auto">
      <Button
        variant="livekit"
        onClick={onLinkMeeting}
        aria-label="Chia sẻ"
        className="shrink-0"
      >
        <Share2 className="size-4 sm:size-5" />
        <span className="ml-1 sm:ml-2 hidden sm:inline text-xs sm:text-sm">
          Chia sẻ
        </span>
      </Button>
      <div className="flex items-center gap-1 sm:gap-2">
        <div className="relative flex items-center">
          <div
            className={`flex items-center h-8 sm:h-9 bg-gray-900 rounded-full px-1.5 sm:px-2 pl-2 sm:pl-4 ${isMicEnabled
                ? "border border-primary"
                : "border border-transparent"
              }`}
          >
            <div
              className={`flex items-center ${isActionsLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${isMicEnabled ? "text-primary" : "text-[#BE123C]"
                }`}
              onClick={isActionsLocked ? undefined : handleToggleMic}
              aria-disabled={isActionsLocked}
            >
              {isMicEnabled ? (
                <Mic className="size-4 sm:size-5" />
              ) : (
                <MicOff className="size-4 sm:size-5" />
              )}
              <span className="ml-1 sm:ml-2 hidden xl:inline text-xs sm:text-sm">
                Microphone
              </span>
            </div>
            <DropdownMenu
              open={isMicrophoneMenuOpen}
              onOpenChange={setIsMicrophoneMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-full ml-1 hover:bg-transparent hover:text-primary"
                  aria-label="Chọn microphone"
                  disabled={isActionsLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMicrophoneMenuOpen(true);
                  }}
                >
                  <ChevronsUpDown
                    className={cn(
                      isMicEnabled ? "text-primary" : "text-[#BE123C]",
                      "size-4"
                    )}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 max-h-64 overflow-y-auto"
              >
                <div className="px-2 py-1.5 text-sm font-semibold">
                  Chọn microphone
                </div>
                {microphones.length === 0 ? (
                  <DropdownMenuItem disabled>
                    Không tìm thấy microphone
                  </DropdownMenuItem>
                ) : (
                  microphones.map((mic) => (
                    <DropdownMenuItem
                      key={mic.deviceId}
                      onClick={() => handleSelectMicrophone(mic.deviceId)}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <span className="flex-1 truncate">
                        {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                      </span>
                      {selectedMicrophoneId === mic.deviceId && (
                        <Check className="size-4 ml-2" />
                      )}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="relative flex items-center">
          <div
            className={`flex items-center h-8 sm:h-9 bg-gray-900 rounded-full px-1.5 sm:px-2 pl-2 sm:pl-4 ${isCameraEnabled
                ? "border border-primary"
                : "border border-transparent"
              }`}
          >
            <div
              className={`flex items-center ${isActionsLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${isCameraEnabled ? "text-primary" : "text-[#BE123C]"
                }`}
              onClick={isActionsLocked ? undefined : handleToggleCamera}
              aria-disabled={isActionsLocked}
            >
              {isCameraEnabled ? (
                <Video className="size-4 sm:size-5" />
              ) : (
                <VideoOff className="size-4 sm:size-5" />
              )}
              <span className="ml-1 sm:ml-2 hidden xl:inline text-xs sm:text-sm">
                Camera
              </span>
            </div>
            <DropdownMenu
              open={isCameraMenuOpen}
              onOpenChange={setIsCameraMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-full ml-1 hover:bg-transparent hover:text-primary"
                  aria-label="Chọn camera"
                  disabled={isActionsLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCameraMenuOpen(true);
                  }}
                >
                  <ChevronsUpDown
                    className={cn(
                      isCameraEnabled ? "text-primary" : "text-[#BE123C]",
                      "size-4"
                    )}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 max-h-72 overflow-y-auto"
              >
                {/* CAMERA LIST */}
                <div className="px-2 py-1.5 text-sm font-semibold">
                  Chọn camera
                </div>

                {cameras.map((camera) => (
                  <DropdownMenuItem
                    key={camera.deviceId}
                    onClick={() =>
                      handleSelectCamera(camera.deviceId, cameraQuality)
                    }
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex-1 truncate">
                      {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                    </span>
                    {selectedCameraId === camera.deviceId && (
                      <Check className="size-4 ml-2" />
                    )}
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />

                {/* QUALITY */}
                <div className="px-2 py-1.5 text-sm font-semibold">
                  Chất lượng camera
                </div>

                <div className="px-2 py-2"><label className="text-sm">FPS ô chính
                  <select aria-label="FPS camera của tôi" className="ml-2 rounded border bg-background p-1" value={cameraResolutionPolicy?.targets[localParticipant.identity]?.configuredFps ?? 30} disabled={isActionsLocked || !meeting} onChange={async e => {
                    if (!meeting) return;
                    try { await updateCameraResolution(meeting.id, undefined, undefined, Number(e.target.value)); toast.success('Đã lưu FPS camera'); }
                    catch { toast.error('Không cập nhật được FPS'); }
                  }}>{[5,10,15,20,24,25,30,60].map(n => <option key={n} value={n}>{n} fps</option>)}</select>
                </label></div>
                {CAMERA_PRESETS.map((preset) => (
                  <DropdownMenuItem
                    key={preset.id}
                    onClick={() => {
                      if (selectedCameraId) {
                        handleSelectCamera(selectedCameraId, preset.id);
                      } else {
                        setCameraQuality(preset.id);
                      }
                    }}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span>{preset.label}</span>
                    {cameraQuality === preset.id && (
                      <Check className="size-4 ml-2" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>

            </DropdownMenu>
          </div>
        </div>

        {currentUserCanShare && (
          <Button
            variant="livekit"
            onClick={handleShareScreen}
            aria-label="Chia sẻ màn hình"
            className={cn(isScreenShareEnabled && "bg-primary/20", "shrink-0")}
            disabled={isActionsLocked}
          >
            <Monitor className="size-4 sm:size-5" />
            {isScreenShareEnabled ? (
              <span className="hidden xl:inline text-xs sm:text-sm">
                Dừng chia sẻ màn hình
              </span>
            ) : (
              <span className="hidden xl:inline text-xs sm:text-sm">
                Chia sẻ màn hình
              </span>
            )}
          </Button>
        )}

        <Button
          variant="livekit"
          onClick={onToggleChat}
          aria-label="Trò chuyện"
          className="shrink-0"
        >
          <MessageSquare className="size-4 sm:size-5" />
          <span className="hidden xl:inline text-xs sm:text-sm">
            Trò chuyện
          </span>
        </Button>
        <Button
          variant="livekit"
          onClick={handleToggleRaiseHand}
          aria-label={currentUserRaisedHand ? "Hạ tay" : "Giơ tay"}
          className={cn(
            "shrink-0",
            currentUserRaisedHand && "bg-green-600 text-white hover:bg-green-600/90"
          )}
        >
          <Hand className="size-4 sm:size-5" />
          <span className="hidden xl:inline text-xs sm:text-sm">
            {currentUserRaisedHand ? "Hạ tay" : "Giơ tay"}
          </span>
        </Button>
        {isHost && meeting?.allowRecording && (
          <Button
            variant="livekit"
            onClick={() => setIsRecordSheetOpen(true)}
            aria-label="Ghi lại"
            className="shrink-0"
            permissionCode="record_meeting"
          >
            <Circle className="size-4 sm:size-5" />
            <span className="hidden xl:inline text-xs sm:text-sm">
              Ghi lại
            </span>
          </Button>
        )}
        {isHost && (
          <Button
            variant="livekit"
            onClick={() => setIsStreamSheetOpen(true)}
            aria-label="Stream"
            className="shrink-0"
            permissionCode="stream_meeting"
          >
            <Radio className="size-4 sm:size-5" />
            <span className="hidden xl:inline text-xs sm:text-sm">
              Stream
            </span>
          </Button>
        )}
        <Button
          variant="livekitDestructive"
          onClick={onLeave}
          aria-label={isHost ? "Kết thúc / rời phòng" : "Rời phòng"}
          className="ml-1 sm:ml-2 shrink-0"
        >
          <LogOut className="size-4 sm:size-5" />
          <span className="hidden xl:inline text-xs sm:text-sm">{isHost ? "Kết thúc" : "Rời phòng"}</span>
        </Button>
      </div>
      {isHost ? (
        <Button
          variant="livekit"
          onClick={onToggleControlPanel}
          aria-label="Điều khiển"
          className="shrink-0"
        >
          <Settings className="size-4 sm:size-5" />
          <span className="hidden sm:inline text-xs sm:text-sm">
            Điều khiển
          </span>
        </Button>
      ) : (
        <div></div>
      )}
      <RecordSheet
        open={isRecordSheetOpen}
        onOpenChange={setIsRecordSheetOpen}
        meeting={meeting}
        isHost={isHost}
      />
      <StreamSheet
        open={isStreamSheetOpen}
        onOpenChange={setIsStreamSheetOpen}
        meeting={meeting}
        isHost={isHost}
      />
    </div>
  );
}
